import Scene from '@scene';
import { 
  setStandOffDimension, 
  setBasePlateOptions, 
  setWoodBracketOptions as setSelectorWoodBracketOptions 
} from '@reducers/selectors/dimension/actions';
import { 
  setWoodPanelOptions as setSelectorWoodPanelOptions
} from '@reducers/selectors/application/actions';
import Panel from '@scene/objects/panel';
import { setBeamShiftX, setBeamShiftY } from '../beam/actions';
import { setWoodConnectionLoadValue, updateCombination } from '@reducers/selectors/loads/actions';
import { APP_TYPES } from '@config';
import loadingParams from '@views/Loads/GeneralLoading/DesignLoading/misc/loadingParams';
import { mergeDeep } from '@services/helper.service';

export const SET_PANEL_TYPE = 'SET_PANEL_TYPE';
export const SET_PANEL_CLOSE = 'SET_PANEL_CLOSE';
export const DISABLE_PANEL_EDGE_DISTANCE = 'DISABLE_PANEL_EDGE_DISTANCE';
export const SET_PANEL_POSITION = 'SET_PANEL_POSITION';
export const SET_PANEL_DPL = 'SET_PANEL_DPL';
export const SET_PANEL_LYH = 'SET_PANEL_LYH';
export const SET_PANEL_DPP = 'SET_PANEL_DPP';
export const SET_PANEL_WIDTH = 'SET_PANEL_WIDTH';
export const SET_PANEL_HEIGHT = 'SET_PANEL_HEIGHT';
export const SET_PANEL_DEPTH = 'SET_PANEL_DEPTH';
export const SET_DY = 'SET_DY';
export const RESET_PANEL = 'RESET_PANEL';
export const SET_PANEL_OPTIONS = 'SET_PANEL_OPTIONS';
export const SET_COMPRESSION_OPTIONS = 'SET_COMPRESSION_OPTIONS';
export const SET_PANEL_SIDE_LENGTH = 'SET_PANEL_SIDE_LENGTH';
export const SET_PANEL_STATE = 'SET_PANEL_STATE';

/*
  Action creators
 */
export function setPanelType(panelType) {
  return (dispatch, getState) => {
    if (getState().options.panel.type === Panel.types.CUSTOM) {
      var oldCenter = {
        x: getState().options.panel.center && getState().options.panel.center.x || 0,
        y: getState().options.panel.center && getState().options.panel.center.y || 0
      };
      dispatch(setBeamShiftX(getState().options.beam.shift.x - oldCenter.x));
      dispatch(setBeamShiftY(getState().options.beam.shift.y + oldCenter.y));
      dispatch(setPanelOptions({
        center: { x: 0, y: 0 }
      }));
    }
    
    dispatch(triggerUpdate({ type: SET_PANEL_TYPE, panelType }));
  };
}

export function setPanelClose(isClosed) {
  return triggerUpdate({ type: SET_PANEL_CLOSE, isClosed });
}

export function disablePanelEdgeDistance(isEdgeDistance) {
  return triggerUpdate({ type: DISABLE_PANEL_EDGE_DISTANCE, isEdgeDistance });
}

export function setPanelPosition(position) {
  return triggerUpdate({ type: SET_PANEL_POSITION, position });
}

export function setPanelDPL(dpl) {
  return (dispatch, getState) => {
    let newDpl = { 
      active: dpl.hasOwnProperty('active') ? dpl.active : getState().options.panel.dpl.active,
      value: dpl.hasOwnProperty('value') ? dpl.value : getState().options.panel.dpl.value
    }; 
    dispatch(setBasePlateOptions({
      dpl: newDpl.active ? newDpl.value : 0
    }));
    dispatch(triggerUpdate({ 
      type: SET_PANEL_DPL, 
      dpl: newDpl
    }));
  };
}

export function setPanelLYH(lyh) {
  return (dispatch, getState) => {
    let newLyh = { 
      active: lyh.hasOwnProperty('active') ? lyh.active : getState().options.panel.lyh.active,
      value: lyh.hasOwnProperty('value') ? lyh.value : getState().options.panel.lyh.value
    }; 
    dispatch(setBasePlateOptions({
      lyh: newLyh.active ? newLyh.value : 0
    }));
    dispatch(triggerUpdate({ 
      type: SET_PANEL_LYH, 
      lyh: newLyh
    }));
  };
}

export function setPanelDPP(dpp) {
  return (dispatch, getState) => {
    let newDpp = { 
      active: dpp.hasOwnProperty('active') ? dpp.active : getState().options.panel.dpp.active,
      value: dpp.hasOwnProperty('value') ? dpp.value : getState().options.panel.dpp.value
    }; 
    dispatch(setBasePlateOptions({
      dpp: newDpp.active ? newDpp.value : 0
    }));
    dispatch(triggerUpdate({ 
      type: SET_PANEL_DPP, 
      dpp: newDpp
    }));
  };
}

export function setPanelWidth(value) {
  return (dispatch) => {
    dispatch(setBasePlateOptions({
      lx: value
    }));
    dispatch(triggerUpdate({ type: SET_PANEL_WIDTH, value }));
  };
}

export function setPanelHeight(value) {
  return (dispatch) => {
    dispatch(setBasePlateOptions({
      ly: value
    }));
    dispatch(triggerUpdate({ type: SET_PANEL_HEIGHT, value }));
  };
}

export function setPanelDepth(value) {
  return (dispatch) => {
    dispatch(setBasePlateOptions({
      tfix: value
    }));
    dispatch(triggerUpdate({ type: SET_PANEL_DEPTH, value }));
  };
}

export function setPanelSideLength(value) {
  return (dispatch) => {
    dispatch(setBasePlateOptions({
      sl: value
    }));
    dispatch(triggerUpdate({ type: SET_PANEL_SIDE_LENGTH, value}));
  };
}

export function setDy(dy) {
  return (dispatch, getState) => {
    let newDy = { 
      isCustom: dy.hasOwnProperty('isCustom') ? dy.isCustom : getState().options.panel.dy.isCustom,
      value: dy.hasOwnProperty('value') ? dy.value : getState().options.panel.dy.value
    }; 
    dispatch(setBasePlateOptions({
      spacing: {
        dy: newDy.value
      }
    }));
    dispatch(triggerUpdate({ 
      type: SET_DY, 
      dy: newDy
    }));
  };
}

export function setLoads(loads) {
  return (dispatch, getState) => {
    if (getState().main.appType === APP_TYPES.GENERAL_CALCULATION) {
      // for import, modify the load based on the header
      if (getState().selectors.loads[getState().main.appType].importState.isImporting) { 
        let headers = getState().selectors.loads[getState().main.appType].importState.headers;
        let updateObj = {};
        Object.getOwnPropertyNames(loads).forEach(load => {
          let index = headers.findIndex(header => header.includes(load));
          if (index !== -1) {
            updateObj[loadingParams[index].key] = loads[load] * (headers[index].includes('-') ? -1 : 1);
          }
        });
        dispatch(
          updateCombination(
            getState().selectors.loads[getState().main.appType].activeCombinationIndex, 
            updateObj
        ));                                                                                                     
      } else {
        dispatch(updateCombination(getState().selectors.loads[getState().main.appType].activeCombinationIndex, loads));
      }
    } else if (getState().main.appType === APP_TYPES.WOOD_CONNECTION) {
      if (loads.hasOwnProperty('faxd')) {
        dispatch(setWoodConnectionLoadValue(loads.faxd));
        dispatch(triggerUpdate({ type: SET_PANEL_OPTIONS, options: { loads } }));
      }
    }
  };
}

export function setPanelOptions(opt, ignoreObjectUpdate) {
  return (dispatch, getState) => {
    var options = mergeDeep({}, opt); // make sure we don't delete the properties from original object due to the reference
		const isPredefinedBasePlate = (getState().selectors.application[getState().main.appType].predefinedBasePlate && 
      getState().selectors.application[getState().main.appType].predefinedBasePlate.isPredefinedBasePlate) || false;
    if (options.hasOwnProperty('standOff') && options.standOff.hasOwnProperty('value')){
      dispatch(setStandOffDimension(options.standOff.value));
      // dispatch(triggerUpdate({ type: SET_PANEL_OPTIONS, options }));
    } 
    if (options.hasOwnProperty('width')){
      if (isPredefinedBasePlate)
        delete options.width;
      else
        dispatch(setPanelWidth(parseFloat(options.width)));
    }
    if (options.hasOwnProperty('height')){
      if (isPredefinedBasePlate)
        delete options.height;
      else
        dispatch(setPanelHeight(parseFloat(options.height)));
    }
    if (options.hasOwnProperty('depth')){
      if (isPredefinedBasePlate)
        delete options.depth;
      else
        dispatch(setPanelDepth(parseFloat(options.depth)));
    }
    if (options.hasOwnProperty('dpp')){
      dispatch(setPanelDPP(options.dpp));
    }
    if (options.hasOwnProperty('dpl')){
      dispatch(setPanelDPL(options.dpl));
    }
    if (options.hasOwnProperty('dy')){
      if (isPredefinedBasePlate)
        delete options.dy;
      else
        dispatch(setDy(options.dy));
    }
    if (options.hasOwnProperty('loads')) {
      dispatch(setLoads(options.loads));
      delete options.loads;
    }
    if (options.hasOwnProperty('lyh')) {
      if (!isPredefinedBasePlate)
        dispatch(setPanelLYH({ active: true, value: options.lyh}));
      delete options.lyh; // prevent another update
    }
    if (options.hasOwnProperty('dimensions') || options.hasOwnProperty("thickness")){
      if (options.dimensions.hasOwnProperty('b'))
        dispatch(setPanelHeight(parseFloat(options.dimensions.b)));
      if (options.dimensions.hasOwnProperty('d'))
        dispatch(setPanelWidth(parseFloat(options.dimensions.d)));
      dispatch(setSelectorWoodBracketOptions(options));
    }
    if (options.hasOwnProperty('wood')){
      dispatch(setSelectorWoodPanelOptions({ woodPanel: { ...options.wood }}));
    }

    if (ignoreObjectUpdate) {
      dispatch({ type: SET_PANEL_OPTIONS, options });
    } else {
      return dispatch(triggerUpdate({ type: SET_PANEL_OPTIONS, options }));
    }
  };
}

export function resetPanel(appType) {
  return (dispatch, getState) => {
    dispatch(triggerUpdate({ type: RESET_PANEL, appType, country: getState().main.localization.country }));
  };
}

export function setCompressionZone(hasCompressionZone, tensionZone, compressionZone, compressionLine){
  return (dispatch, getState) => {
    dispatch(triggerUpdate({ 
      type: SET_COMPRESSION_OPTIONS, 
      options: {
        hasCompressionZone, 
        tensionZone, 
        compressionZone,
        compressionLine
      }
    }));
  };
}

export function setPanelState(state) {
  return (dispatch, getState) => {
    dispatch(triggerUpdate({ type: SET_PANEL_STATE, state }));
  };
}

function triggerUpdate(event) {
  return (dispatch, getState) => {
    dispatch(event);

    Scene.updatePanel(getState().options.panel);
  };
}



// WEBPACK FOOTER //
// ./src/reducers/options/panel/actions.js